import type { Dayjs } from 'dayjs';
import {
  type CSSProperties,
  type MouseEvent,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css, keyframes } from 'styled-components';

import TicketOpenOutlined from '@/assets/icons/TicketOpenOutlined';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import TransitionPortal from '@/components/Common/TransitionPortal';
import { StyledTooltip } from '@/components/Tooltip/TooltipRenderer';
import { MainText } from '@/components/Typography/NamedTypo';
import useColor from '@/hooks/domain/useColor';
import { getAlphaColor } from '@/styles/colors/color.utils';
import {
  type FeedbackSentiment,
  sentimentMap,
  SOURCE_STATUS,
} from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

const SENTIMENT_CIRCLE_RADIUS = 12;
const SENTIMENT_TREND_CHART_TOOLTIP_ID = 'sentiment-trend-chart-tooltip';

type Props = {
  data: SentimentDistItemT[];
  onChartSentenceClick?: (alias: string) => void;
  chartStyle?: CSSProperties;
  startDate: Dayjs;
  endDate: Dayjs;
  offset?: number;
  radius?: number;
};

export const CustomerSentimentTrendChart = (props: Props) => {
  const radius = props.radius || SENTIMENT_CIRCLE_RADIUS;
  const color = useColor();
  const [closetSentimentCircleIndex, setClosentSentimentCircleIndex] = useState<
    number | null
  >(null);
  const refsArray = useRef<HTMLDivElement[]>([]);

  const [currTooltipInfo, setCurrTooltipInfo] = useState<{
    index: number;
    date: string;
    sentiment: FeedbackSentiment;
    isOpenTicket: boolean;
    pos: CSSProperties;
  } | null>(null);

  const setRef = (element: HTMLDivElement | null) => {
    if (!element) return;
    const index = Number(element.dataset['index']);
    refsArray.current[index] = element;
    return element;
  };

  /**
   * Calculates the position of the sentiment circle based on the index.
   * @param index - The index of the data point.
   * @returns The position of the sentiment circle as a percentage.
   */
  const getSentimentCirclePos = (index: number) => {
    const total = props.endDate.diff(props.startDate, 'second');
    const current = getTimezoneDayjs(props.data[index].date).diff(
      props.startDate,
      'second',
    );
    return (current / total) * 100;
  };

  const onMouseMove: MouseEventHandler = (e: MouseEvent) => {
    // get the closest sentiment circle to the mouse position
    const x = e.clientX;
    const y = e.clientY;

    let closestIndex = -1;
    let closestDistance = Infinity;

    refsArray.current.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const distance = Math.sqrt(
        Math.pow(x - rect.left - rect.width / 2, 2) +
          Math.pow(y - rect.top - rect.height / 2, 2),
      );

      if (distance < closestDistance) {
        closestIndex = index;
        closestDistance = distance;
      }
    });

    if (closestIndex === -1) return;

    // if current closest sentiemnt circle is different from the previous one
    if (closestIndex !== closetSentimentCircleIndex) {
      // reset the previous closest sentiment circle
      resetClosestSentimentCircle();

      // update the closest sentiment circle index
      setClosentSentimentCircleIndex(closestIndex);
    }
  };

  const onMouseLeave = () => {
    // reset the opacity of all sentiment circles
    resetClosestSentimentCircle();

    // clear the closest sentiment circle
    setClosentSentimentCircleIndex(null);
  };

  const onMouseClick = () => {
    if (closetSentimentCircleIndex === null) return;

    const closestSentimentCircle =
      refsArray.current[closetSentimentCircleIndex];
    if (!closestSentimentCircle) return;

    props.onChartSentenceClick?.(props.data[closetSentimentCircleIndex].alias);
  };

  const activateClosestSentimentCircle = useCallback(() => {
    if (closetSentimentCircleIndex === null) return;

    const closestSentimentCircle =
      refsArray.current[closetSentimentCircleIndex];
    if (!closestSentimentCircle) return;

    closestSentimentCircle.style.opacity = '1';
    closestSentimentCircle.style.zIndex = '1';
    closestSentimentCircle.style.outline = `1px solid ${color.gray600}`;
  }, [closetSentimentCircleIndex, color.gray600]);

  const resetClosestSentimentCircle = () => {
    if (closetSentimentCircleIndex === null) return;

    const closestSentimentCircle =
      refsArray.current[closetSentimentCircleIndex];
    if (!closestSentimentCircle) return;

    closestSentimentCircle.style.opacity = '0.6';
    closestSentimentCircle.style.zIndex = '0';
    closestSentimentCircle.style.outline = 'none';
  };

  useEffect(() => {
    if (closetSentimentCircleIndex === null) return;

    activateClosestSentimentCircle();
  }, [closetSentimentCircleIndex, activateClosestSentimentCircle]);

  return (
    <StyledChart
      offset={props.offset}
      style={props.chartStyle}
      onClick={onMouseClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <div className="horizontal-axis">
        <Flex className="sentiments-container" a="center">
          {props.data.map((dataItem, index, arr) => {
            const sentiment = (dataItem.value || 2) as FeedbackSentiment;

            // type guard
            if (!sentimentMap[sentiment]) {
              return null;
            }

            return (
              <Flex
                key={dataItem.alias}
                style={{
                  position: 'absolute',
                  left: `calc(${getSentimentCirclePos(index)}% - ${
                    radius / 2
                  }px)`,
                }}
              >
                <StyledSentimentCircle
                  data-index={index}
                  data-tooltip-id={SENTIMENT_TREND_CHART_TOOLTIP_ID}
                  data-tooltip-place="top"
                  ref={setRef}
                  radius={radius}
                  className="sentiment"
                  sentiment={sentiment}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCurrTooltipInfo({
                      index,
                      sentiment,
                      date: dataItem.date,
                      isOpenTicket:
                        dataItem.source_status === SOURCE_STATUS.OPEN,
                      pos: {
                        top: rect.top - 60,
                        left: rect.left,
                      },
                    });
                  }}
                  onMouseLeave={() => setCurrTooltipInfo(null)}
                  hasBounce={dataItem.source_status === SOURCE_STATUS.OPEN}
                />
              </Flex>
            );
          })}
          <TransitionPortal
            status={closetSentimentCircleIndex === currTooltipInfo?.index}
            layerId="tooltip-layer"
            background={{
              enable: false,
            }}
          >
            {currTooltipInfo && (
              <StyledTooltip
                style={{
                  position: 'fixed',
                  backgroundColor: color.gray100,
                  boxShadow: 'none',
                  border: `1px solid ${color.gray300}`,
                  borderRadius: 8,
                  ...currTooltipInfo.pos,
                }}
              >
                <VFlex g={8}>
                  <MainText dim>
                    {getTimezoneDayjs(currTooltipInfo.date).format(
                      DATE_FORMAT.readable,
                    )}
                  </MainText>
                  <Flex a="center" g={8}>
                    {sentimentMap[currTooltipInfo.sentiment].icon}
                    <MainText>
                      {sentimentMap[currTooltipInfo.sentiment].value}
                    </MainText>
                  </Flex>
                  {currTooltipInfo.isOpenTicket && (
                    <Flex a="center" g={8}>
                      <TicketOpenOutlined className="text-destructive" />
                      <MainText className="text-destructive">Open</MainText>
                    </Flex>
                  )}
                </VFlex>
              </StyledTooltip>
            )}
          </TransitionPortal>
        </Flex>
      </div>
    </StyledChart>
  );
};

type StyledChartProps = {
  offset?: number;
};

const StyledChart = styled(CenterFlex)<StyledChartProps>`
  border-radius: 8px;
  height: 38px;
  width: 100%;
  cursor: pointer;

  .horizontal-axis {
    height: 1px;
    background-color: ${(props) => props.theme.colors.gray250};
    position: relative;
    padding: 0 ${(props) => props.offset || 16}px;
    width: 100%;
  }

  .sentiments-container {
    position: relative;
  }
`;

type StyledSentimentCircleProps = {
  radius: number;
  sentiment: FeedbackSentiment;
  hasBounce?: boolean;
};

const SentimentCirlceAnimation = (props: any) => keyframes`
  0% {
    box-shadow: 0px 0px 0px 0px ${getAlphaColor(
      props.theme.colors.sentiment[props.sentiment],
      0.8,
    )};
  }
  60% {
    box-shadow: 0px 0px 0px 10px ${getAlphaColor(
      props.theme.colors.sentiment[props.sentiment],
      0,
    )};
  }
  100% {
    box-shadow: 0px 0px 0px 10px ${getAlphaColor(
      props.theme.colors.sentiment[props.sentiment],
      0,
    )};
  }
`;

const StyledSentimentCircle = styled(CenterFlex)<StyledSentimentCircleProps>`
  position: relative;
  aspect-ratio: 1;
  width: ${(props) => props.radius}px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.sentiment[props.sentiment]};
  opacity: 0.6;
  ${(props) =>
    props.hasBounce &&
    css`
      animation: ${(props) => SentimentCirlceAnimation(props)} 2s ease-out
        infinite;
    `}
`;
